/**
 * Checklist Service
 * Service layer for checklist-related API operations
 * Integrates with AWS Lambda functions for checklist management
 */

import { apiService, ApiError } from "./api.js";
import {
  transformChecklistForLambda,
  transformLambdaResponseToFrontend,
  validateChecklistForLambda,
  transformQueryParamsForBackend,
  createEmptyChecklistStructure,
} from "../utils/checklistDataTransforms.js";

/**
 * Checklist service class
 */
export class ChecklistService {
  constructor() {
    this.apiService = apiService;
    this.basePath = "/checklist";
  }

  /**
   * Get checklist data from backend
   * @param {Object} queryParams - Query parameters
   * @param {string} queryParams.templateId - Template ID
   * @param {string} queryParams.saId - Service Appointment ID (maps to serviceAppointmentNumber)
   * @param {string} [queryParams.templateVersion] - Template version
   * @returns {Promise<Object>} Transformed checklist data
   */
  async getChecklist(queryParams) {
    try {
      // Validate required parameters
      if (!queryParams.templateId) {
        throw new ApiError("Checklist ID (templateId) is required", 400);
      }
      if (!queryParams.saId) {
        throw new ApiError("Service Appointment ID is required", 400);
      }
      if (!queryParams.templateVersion) {
        throw new ApiError("Template version is required", 400);
      }

      // Transform frontend parameters to backend format
      const backendParams = transformQueryParamsForBackend(queryParams);

      console.log(
        "ChecklistService.getChecklist - Backend params:",
        backendParams
      );

      // Make API request
      const response = await this.apiService.get(`${this.basePath}/get`, {
        params: { ...backendParams },
      });

      console.log("ChecklistService.getChecklist - Raw response:", response);

      // Transform backend response to frontend format
      return transformLambdaResponseToFrontend(response.data);
    } catch (error) {
      console.error("ChecklistService.getChecklist failed:", error);
      throw new ApiError(
        "Failed to load checklist. Please try again.",
        error.statusCode || 500,
        { operation: "getChecklist", queryParams }
      );
    }
  }

  /**
   * Save checklist data to backend
   * @param {Object} checklistData - Checklist data to save
   * @returns {Promise<Object>} Save response
   */
  async saveChecklist(checklistData, workStepId) {
    try {
      // Validate checklist data
      const validation = validateChecklistForLambda(checklistData);
      if (!validation.isValid) {
        throw new ApiError(
          `Invalid checklist data: ${validation.errors.join(", ")}`,
          400,
          { validation }
        );
      }

      // Transform frontend data for backend
      const backendData = transformChecklistForLambda(checklistData);

      console.log(
        "ChecklistService.saveChecklist - Backend data:",
        backendData
      );

      // Make API request
      const response = await this.apiService.post(`${this.basePath}/save`, {
        checklist_data: backendData,
        work_step_id: workStepId,
      });

      console.log("ChecklistService.saveChecklist - Response:", response);

      return response.data;
    } catch (error) {
      console.error("ChecklistService.saveChecklist failed:", error);
      throw new ApiError(
        "Failed to save checklist. Please try again.",
        error.statusCode || 500,
        { operation: "saveChecklist", checklistData }
      );
    }
  }

  /**
   * Get filtered checklists (if needed in the future)
   * @param {Object} filters - Filter criteria
   * @returns {Promise<Array>} Array of filtered checklists
   */
  async getFilteredChecklists(filters = {}) {
    try {
      // This would integrate with the checklistGetFilteredChecklists Lambda function
      // For now, return empty array as this functionality isn't implemented yet
      console.warn("getFilteredChecklists not yet implemented");
      return [];
    } catch (error) {
      console.error("ChecklistService.getFilteredChecklists failed:", error);
      throw new ApiError(
        "Failed to load filtered checklists. Please try again.",
        error.statusCode || 500,
        { operation: "getFilteredChecklists", filters }
      );
    }
  }

  /**
   * Save checklist data to backend
   * @param {Object} pdfParams - Parameters used to retrieve data for the composition of the PDF
   * @returns {Promise<Object>} Save response
   */
  async savePdf(pdfParams = {}) {
    try {
      if (
        Object.keys(pdfParams).length === 0 ||
        !pdfParams.hasOwnProperty("caseId") ||
        !pdfParams.hasOwnProperty("serviceAppointmentNumber")
      ) {
        throw new ApiError(
          `Missing parameters for the creation of the PDF file`,
          400,
          {}
        );
      }

      // Transform frontend data for backend
      const body = {
        case_id: pdfParams["caseId"],
        service_appointment_number: pdfParams["serviceAppointmentNumber"],
        work_step_id: pdfParams["workStepId"],
      };

      console.log("ChecklistService.savePdf - Parameters", body);

      // Make API request
      const response = await this.apiService.post(
        `${this.basePath}/postPdf`,
        body
      );

      console.log("ChecklistService.savePdf - Response:", response);

      return response.data;
    } catch (error) {
      console.error("ChecklistService.savePdf failed:", error);
      throw new ApiError(
        "Failed to save checklist PDF file. Please try again.",
        error.statusCode || 500,
        { operation: "savePdf", pdfParams }
      );
    }
  }
}

// Create and export default checklist service instance
export const checklistService = new ChecklistService();

export default checklistService;
